import { PlaybackClock } from '../animation/clock';
import { Observable } from '../lib/store';

/**
 * Playback state shared by the Animation workspace, the timeline and prompts. It owns
 * the single animation clock (see animation/clock.ts): buttons and prompt commands change
 * the clock, and the mounted renderer reads it. The clock outlives renderer instances, so
 * "playing" survives navigating away and back. Nothing here is project data: none of it is
 * saved or recorded in undo history.
 */
const DRAFT_EFFECTS_KEY = 'sonicforge.draftEffects';

/** Reads a per-browser preference; storage can be missing or blocked (private windows). */
function readDraftEffects(): boolean {
  try {
    return globalThis.localStorage?.getItem(DRAFT_EFFECTS_KEY) === '1';
  } catch {
    return false;
  }
}

export class AnimationBus {
  readonly clock: PlaybackClock;
  /** Auto-Key: transform and pose edits create keyframes at the playhead. */
  readonly autoKey = new Observable<boolean>(false);
  /**
   * Phase 5B.1: draw glow, shadow and grade at draft quality in the on-screen previews
   * (fewer blur passes, half-resolution filters). Video export always uses full quality.
   * A per-browser preference: kept across projects and reloads, never in the project.
   */
  readonly draftEffects = new Observable<boolean>(readDraftEffects());

  constructor(clock = new PlaybackClock()) {
    this.clock = clock;
  }

  start = () => this.clock.play();
  pause = () => this.clock.pause();
  /** Back to frame 0 (keeps playing if it was). */
  reset = () => this.clock.reset();
  /** Back to frame 0 and play. */
  restart = () => this.clock.restart();
  setAutoKey = (on: boolean) => this.autoKey.set(on);
  setDraftEffects = (on: boolean) => {
    this.draftEffects.set(on);
    try {
      globalThis.localStorage?.setItem(DRAFT_EFFECTS_KEY, on ? '1' : '0');
    } catch {
      // Not remembered when storage is unavailable; the setting still applies now.
    }
  };

  /** Opening or closing a project: stop, rewind, forget the timing, Auto-Key off. */
  clear(): void {
    this.clock.pause();
    this.clock.load(null, null);
    this.clock.reset();
    this.clock.setSpeed(1);
    this.autoKey.set(false);
  }
}
